
// Enhanced CommonJS polyfill for browser environment

console.log("[Enhanced Polyfill] Starting extended CommonJS polyfill initialization");

declare global {
  interface Window {
    React: any;
    ReactDOM: any;
    L: any; // For Leaflet
    require: NodeRequire;
    process: {
      env: { 
        NODE_ENV: string;
        [key: string]: string | undefined;
      };
    };
    global: Window;
    __dirname: string;
    __filename: string;
    __moduleRegistry: Record<string, any>;
  }
}

// Define module object type that matches NodeModule
interface Module {
  id: string;
  exports: any;
  loaded: boolean;
  filename?: string;
  paths?: string[];
  children?: Module[];
  parent?: Module | null;
  path?: string;
  // Add missing properties required by the NodeModule interface
  isPreloading?: boolean;
  require?: NodeRequire;
}

// Import React directly to ensure it's loaded first
import * as React from 'react';
import * as ReactDOM from 'react-dom/client';

console.log("[Enhanced Polyfill] React imported directly in commonjsPolyfill:", {
  createContext: typeof React.createContext === 'function',
  createElement: typeof React.createElement === 'function'
});

// Core modules that might be required early
const coreModules: Record<string, any> = {
  // React ecosystem - Make sure these are properly exported
  'react': React,
  'react-dom': ReactDOM,
  'react-dom/client': ReactDOM,
  'react-router-dom': {},
  
  // Leaflet ecosystem
  'leaflet': window.L || {},
  '@react-leaflet/core': {},
  'react-leaflet': {},
  'leaflet.markercluster': {},
  '@changey/react-leaflet-markercluster': {},
  'leaflet-defaulticon-compatibility': {},

  // Add any other commonly required packages
  '@react-google-maps/api': {},
  'recharts': {},
  'date-fns': {}
};

// Immediate execution - don't wait
(function extendCommonJSPolyfill() {
  console.log("[Enhanced Polyfill] Extending and validating CommonJS environment");
  
  // Explicitly set React and ReactDOM on window if they're not already set properly
  if (!window.React || typeof window.React.createContext !== 'function') {
    window.React = React;
    console.log("[Enhanced Polyfill] Set window.React from imported React:", typeof window.React);
  }
  
  if (!window.ReactDOM || typeof window.ReactDOM.createRoot !== 'function') {
    window.ReactDOM = ReactDOM;
    console.log("[Enhanced Polyfill] Set window.ReactDOM from imported ReactDOM:", typeof window.ReactDOM);
  }
  
  // Log React methods to verify they exist
  if (window.React) {
    console.log("[Enhanced Polyfill] React methods available:", {
      createElement: typeof window.React.createElement === 'function',
      createContext: typeof window.React.createContext === 'function',
      useState: typeof window.React.useState === 'function',
      version: window.React.version
    });
  }
  
  // Verify that window.require exists from the inline script
  if (typeof window.require !== 'function') {
    console.error("[Enhanced Polyfill] Critical error: window.require is not defined!");
    // Create emergency backup require function with all required properties
    const requireFn = function(id: string): any {
      console.warn(`[Emergency Require] Module requested: ${id}`);
      // Special case for React
      if (id === 'react') {
        console.log("[Emergency Require] Returning React explicitly");
        return window.React || React;
      }
      // Special case for ReactDOM
      if (id === 'react-dom' || id === 'react-dom/client') {
        console.log("[Emergency Require] Returning ReactDOM explicitly");
        return window.ReactDOM || ReactDOM;
      }
      
      // Check for pre-registered modules
      if (coreModules[id]) {
        return coreModules[id];
      }
      
      return {};
    } as NodeRequire;
    
    // Add required NodeRequire properties to the function
    requireFn.resolve = (id: string): string => id;
    requireFn.cache = {};
    requireFn.extensions = {};
    requireFn.main = {
      id: '.<main>',
      filename: '',
      path: '',
      exports: {},
      loaded: true,
      require: requireFn
    };
    
    // Assign the properly typed require function
    window.require = requireFn;
  }
  
  // Ensure global objects exist
  window.global = window;
  window.process = window.process || {
    env: { NODE_ENV: 'development' }
  };
  
  // Ensure __dirname and __filename are defined
  window.__dirname = window.__dirname || '/';
  window.__filename = window.__filename || '/index.js';
  
  // Make sure module registry exists
  window.__moduleRegistry = window.__moduleRegistry || {};
  
  // Pre-register React and ReactDOM with the actual imported modules
  window.__moduleRegistry['react'] = {
    id: 'react',
    exports: window.React || React,
    loaded: true,
    require: window.require
  };
  
  window.__moduleRegistry['react-dom'] = {
    id: 'react-dom',
    exports: window.ReactDOM || ReactDOM,
    loaded: true,
    require: window.require
  };
  
  window.__moduleRegistry['react-dom/client'] = {
    id: 'react-dom/client',
    exports: window.ReactDOM || ReactDOM,
    loaded: true,
    require: window.require
  };
  
  // Pre-register other core modules with correct React reference
  Object.entries(coreModules).forEach(([id, exports]) => {
    if (!window.__moduleRegistry[id]) {
      window.__moduleRegistry[id] = {
        id,
        exports,
        loaded: true,
        require: window.require
      };
      console.log(`[Enhanced Polyfill] Pre-registered core module: ${id}`);
    }
  });
  
  // Override window.require to always check for React first
  const originalRequire = window.require;
  window.require = function(id: string): any {
    // Special handling for react and react-dom
    if (id === 'react') {
      console.log("[Enhanced Require] Returning React explicitly");
      return window.React || React;
    }
    if (id === 'react-dom' || id === 'react-dom/client') {
      console.log("[Enhanced Require] Returning ReactDOM explicitly");
      return window.ReactDOM || ReactDOM;
    }
    
    // Normal require for other modules
    return originalRequire(id);
  } as NodeRequire;
  
  // Add all the NodeRequire properties back
  window.require.resolve = originalRequire.resolve;
  window.require.cache = originalRequire.cache;
  window.require.extensions = originalRequire.extensions;
  window.require.main = originalRequire.main;
  
  console.log("[Enhanced Polyfill] CommonJS polyfill extension complete");
})();

// Export validation function
export function ensurePolyfillActive(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.require !== 'function') return false;
  if (typeof window.global !== 'object') return false;
  if (typeof window.process !== 'object') return false;
  
  // Check that require has all needed properties
  const requireOk = typeof window.require.resolve === 'function' && 
                   typeof window.require.cache === 'object' &&
                   typeof window.require.extensions === 'object' &&
                   typeof window.require.main === 'object';
  
  console.log("[Enhanced Polyfill] Validation check:", requireOk ? "PASSED" : "FAILED");
  
  // If validation fails, log details
  if (!requireOk) {
    console.error("[Enhanced Polyfill] require validation failed:", {
      "resolve exists": typeof window.require.resolve === 'function',
      "cache exists": typeof window.require.cache === 'object',
      "extensions exists": typeof window.require.extensions === 'object',
      "main exists": typeof window.require.main === 'object'
    });
  }
  
  return requireOk;
}

// Check React is properly available
export function validateReact(): boolean {
  const reactAvailable = typeof window.React === 'object' && window.React !== null;
  const createContextAvailable = reactAvailable && typeof window.React.createContext === 'function';
  
  console.log("[Enhanced Polyfill] React validation:", {
    "React available": reactAvailable,
    "createContext available": createContextAvailable,
    "React methods": reactAvailable ? Object.keys(window.React).filter(key => typeof window.React[key] === 'function').join(', ') : 'N/A'
  });
  
  if (!reactAvailable || !createContextAvailable) {
    console.error("CRITICAL: React is not properly available! Attempting emergency fix...");
    try {
      // Emergency override - last resort
      window.React = React;
      window.ReactDOM = ReactDOM;
      console.log("Emergency fix applied. React methods now:", 
                 Object.keys(window.React).filter(key => typeof window.React[key] === 'function').join(', '));
    } catch (e) {
      console.error("Failed to apply emergency fix:", e);
    }
    
    return typeof window.React.createContext === 'function';
  }
  
  return createContextAvailable;
}

// Export standalone debugging function
export function debugRequireState(): void {
  console.log("=== CommonJS Polyfill Debug Info ===");
  console.log("window.require exists:", typeof window.require === 'function');
  console.log("window.global exists:", typeof window.global === 'object');
  console.log("window.process exists:", typeof window.process === 'object');
  console.log("window.__moduleRegistry:", Object.keys(window.__moduleRegistry || {}));
  
  if (typeof window.require === 'function') {
    console.log("require.resolve exists:", typeof window.require.resolve === 'function');
    console.log("require.cache exists:", typeof window.require.cache === 'object');
    console.log("require.extensions exists:", typeof window.require.extensions === 'object');
    console.log("require.main exists:", typeof window.require.main === 'object');
    
    // Log registered modules
    const moduleCount = Object.keys(window.__moduleRegistry || {}).length;
    console.log(`Registered modules: ${moduleCount}`);
    if (moduleCount > 0 && moduleCount < 20) {
      console.log("Module IDs:", Object.keys(window.__moduleRegistry || {}));
    }
  }
  
  // Check React specifically
  validateReact();
  
  console.log("================================");
}

// Run validation check immediately
const polyfillActive = ensurePolyfillActive();
console.log("[Enhanced Polyfill] CommonJS polyfill loaded, require available:", polyfillActive);

// Export for use elsewhere
export default function getRequire(): NodeRequire | undefined {
  return typeof window !== 'undefined' ? window.require : undefined;
}
